import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';

import operationService from '../../service/operation.service';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';
import { useLoader } from '../../provider/LoaderProvider';
import useMenus from '../../context/MenuContext';
import { DeleteDialogUI, FormBase, FormButtonPanelUI, FormHeaderUI } from '../Interface';
import FormFields from './FormFields';
import OperationReports from './OperationReports';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import OperationTabs from './OperationTabs';
import useParam from '../../context/ParamContext';
import { isEqual } from 'lodash';
import useConfig from '../../context/ConfigContext';

export const OperationForm = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getParam } = useParam();
  const { getConfig } = useConfig();
  const { showLoader, hideLoader } = useLoader();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    resourceOne: [],
    resourceOneGroup: [],
    companyLocation: [],
    prodStatus: [],
  });
  const [autocompleteData, setAutocompleteData] = useState({});
  const [operationGroupLink, setOperationGroupLink] = useState([]);
  const [operationUserLink, setOperationUserLink] = useState([]);
  const [defaultValues, setDefaultValues] = useState({});
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const def = {
    code: '',
    operationName: '',
    resourceOneGroupId: null,
    resourceOneId: null,
    groupReporting: false,
    multiManOperation: '',
    verificationRequired: false,
    showMaterialItemCode: false,
    companyLocationId: null,
    showDoseNumber: false,
    costPerHour: '',
    useMobileIncoming: false,
    useMobileOutGoing: false,
    mobilTargetStatus: null,
    operationGroupLink: [],
    operationUserLink: [],
    ignoreSequence: false,
  };

  useEffect(() => {
    setDefaultValues(def);
    getMenus();
    initDefaultValues();
    getOperationLinks();
    getParam();
    getConfig();
  }, []);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('operation');
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  const validationSchema = Yup.object().shape({
    code: Yup.string().nullable(),
    operationName: Yup.string().required(),
    resourceOneGroupId: Yup.string().nullable(),
    resourceOneId: Yup.string().nullable(),
    groupReporting: Yup.boolean(),
    multiManOperation: Yup.number().nullable().min(2).max(50),
    verificationRequired: Yup.boolean(),
    showMaterialItemCode: Yup.boolean(),
    companyLocationId: Yup.string().nullable(),
    showDoseNumber: Yup.boolean(),
    costPerHour: Yup.number().positive().nullable(),
    useMobileIncoming: Yup.boolean(),
    useMobileOutGoing: Yup.boolean(),
    mobilTargetStatus: Yup.string()
      .nullable()
      .max(50)
      .when(['useMobileIncoming', 'useMobileOutGoing'], {
        is: (useMobileIncoming, useMobileOutGoing) => Boolean(useMobileIncoming || useMobileOutGoing),
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
    ignoreSequence: Yup.boolean(),
  });

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!isEqual(defaultValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
      }
    } else {
      setOperatorsDisabled(true);
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  const getOperationLinks = () => {
    Promise.all([masterDataService.operationGroupToForm(), masterDataService.usersToForm()])
      .then(([operationGroupLink, operationUserLink]) => {
        setOperationGroupLink(operationGroupLink);
        setOperationUserLink(operationUserLink);
        setDefaultValues((prev) => ({ ...prev, operationGroupLink, operationUserLink }));
        setInitValues((prev) => ({ ...prev, operationGroupLink, operationUserLink }));
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneGroupToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyLocationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .productionStatusToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          resourceOne: resp[0],
          resourceOneGroup: resp[1],
          companyLocation: resp[2],
          prodStatus: resp[3],
        };

        setAutocompleteData(aData);

        const acOptions = {
          resourceOne: generateAutocompleteItems(aData.resourceOne, 'name', 'id'),
          resourceOneGroup: generateAutocompleteItems(aData.resourceOneGroup, 'name', 'id'),
          companyLocation: generateAutocompleteItems(aData.companyLocation, 'name', 'id'),
          prodStatus: generateAutocompleteItems(aData.prodStatus, 'name', 'code'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const valuesToForm = () => {
    const {
      code,
      operationName,
      resourceOneGroupId,
      resourceOneId,
      operationGroupLink: operationGroupLinkValues,
      operationUserLink: operationUserLinkValues,
      groupReporting,
      multiManOperation,
      verificationRequired,
      showMaterialItemCode,
      companyLocationId,
      showDoseNumber,
      costPerHour,
      useMobileIncoming,
      useMobileOutGoing,
      mobilTargetStatus,
      ignoreSequence,
    } = selectedValues;

    const updatedOperationGroupLink = operationGroupLink.map((groupLink) => ({
      ...groupLink,
      checked: operationGroupLinkValues?.some((form) => form.operationGroupId === groupLink.id) || false,
    }));

    const updatedOperationUserLink = operationUserLink.map((userLink) => ({
      ...userLink,
      checked: operationUserLinkValues?.some((form) => form.userId === userLink.userId) || false,
    }));

    setInitValues({
      code: code ?? defaultValues.code,
      operationName,
      resourceOneGroupId: resourceOneGroupId ?? defaultValues.resourceOneGroupId,
      resourceOneId,
      groupReporting,
      multiManOperation: multiManOperation ?? defaultValues.multiManOperation,
      verificationRequired,
      showMaterialItemCode,
      companyLocationId,
      showDoseNumber,
      ignoreSequence,
      costPerHour: costPerHour ?? defaultValues.costPerHour,
      useMobileIncoming,
      useMobileOutGoing,
      mobilTargetStatus: mobilTargetStatus ?? defaultValues.mobilTargetStatus,
      operationUserLink: updatedOperationUserLink,
      operationGroupLink: updatedOperationGroupLink,
    });
  };

  const handleInsertObject = (values) => {
    showLoader();
    const insertObject = {
      ...values,
      multiManOperation: parseInt(values.multiManOperation) ?? null,
      costPerHour: parseFloat(values.costPerHour),
      operationUserLink: values.operationUserLink.filter((link) => link.checked).map((link) => link.userId),
      operationGroupLink: values.operationGroupLink.filter((link) => link.checked).map((link) => link.id),
    };

    operationService
      .createOperation(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRowSelectionModel(data.operationId);
        setSelectedValues(data);
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleSubmit = (values) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      handleInsertObject(values);
    }
  };

  const updateObject = async (values) => {
    const updData = {
      ...values,
      multiManOperation: parseInt(values.multiManOperation) ?? null,
      costPerHour: parseFloat(values.costPerHour),
      operationUserLink: values.operationUserLink.filter((link) => link.checked).map((link) => link.userId),
      operationGroupLink: values.operationGroupLink.filter((link) => link.checked).map((link) => link.id),
    };

    operationService.updateOperation(updData, selectedValues.operationId).then((_operation) => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleRemoveElement = async () => {
    operationService.deleteOperation(selectedValues.operationId).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
      setInitValues(defaultValues);
    });
  };

  return (
    <>
      <FormBase
        name="Operation"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setInitValues(defaultValues);
                  setSelectedValues(defaultValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ resetForm }) => (
                  <Form>
                    <FormFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      autoCompleteOptions={autoCompleteOptions}
                      autocompleteData={autocompleteData}
                    />

                    <OperationTabs disabled={viewField} />

                    <FormButtonPanelUI
                      onCancelClick={() => {
                        setViewField(true);
                        setEditButtonvisible(true);
                        setAddField(true);

                        resetForm();
                      }}
                      disabled={editButtonVisible && addField}
                    />
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <OperationReports
            refetch={{ refetchTable, setRefetchTable }}
            values={{ selectedValues, setSelectedValues }}
            viewField={viewField}
            operatorsDisabled={operatorsDisabled}
            autoCompleteOptions={autoCompleteOptions}
            rowSeletion={{ rowSelectionModel, setRowSelectionModel }}
          />
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
